import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { api } from "../lib/api";
import { Button } from "../components/ui/Button";

interface DeletedEntity {
  id: string;
  name: string;
  country: { name: string; flag: string; region: string } | null;
  region: string;
  deletedAt: string;
  deletedBy: string | null;
}

function useDeletedEntities() {
  return useQuery<DeletedEntity[]>({
    queryKey: ["deleted-entities"],
    queryFn: async () => {
      const res = await api.get<DeletedEntity[]>("/admin/deleted-entities");
      return res.data;
    },
  });
}

interface RestoreErrorDetail {
  error?: string;
  conflicting_id?: string;
}

function RestoreButton({ entity }: { entity: DeletedEntity }): JSX.Element {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation(["admin", "common"]);

  const mutation = useMutation({
    mutationFn: () => api.post(`/admin/entities/${entity.id}/restore`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["entities"] });
      void queryClient.invalidateQueries({ queryKey: ["entities-all"] });
      void queryClient.invalidateQueries({ queryKey: ["renewals"] });
      void queryClient.invalidateQueries({ queryKey: ["deleted-entities"] });
      toast.success(t("admin:restore_success"));
      setOpen(false);
    },
    onError: (err: unknown) => {
      const detail = (
        err as { response?: { data?: RestoreErrorDetail } }
      )?.response?.data;
      if (detail?.error === "name_already_taken") {
        toast.error(
          t("admin:restore_name_clash", { conflicting_id: detail.conflicting_id ?? "unknown" }),
          { duration: 8000 },
        );
      } else if (detail?.error === "not_deleted") {
        toast.success(t("admin:already_restored"));
        void queryClient.invalidateQueries({ queryKey: ["deleted-entities"] });
      } else {
        toast.error(t("admin:restore_error"));
      }
      setOpen(false);
    },
  });

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <Button variant="secondary" type="button">
          {t("common:restore")}
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50
                     bg-surface rounded-[11px] border border-border shadow-pop
                     p-6 w-[min(92vw,380px)] focus:outline-none"
          aria-describedby="restore-entity-desc"
        >
          <Dialog.Title className="font-display font-semibold text-[15px] tracking-[-0.01em] text-text mb-2">
            {t("admin:restore_title", { name: entity.name })}
          </Dialog.Title>
          <Dialog.Description id="restore-entity-desc" className="text-[13px] text-muted mb-5">
            {t("admin:restore_desc")}
          </Dialog.Description>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setOpen(false)}
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? t("admin:restoring_label") : t("common:restore")}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default function AdminDeletedEntities(): JSX.Element {
  const { data = [], isLoading } = useDeletedEntities();
  const { t } = useTranslation(["admin", "common"]);
  const navigate = useNavigate();

  // Row navigation via onClick instead of wrapping the row in <Link>: an <a>
  // cannot legally contain a <tr> (or the interactive Restore <button>), and
  // the invalid nesting made a Restore click navigate the row instead of
  // opening the dialog. Keyboard-accessible via role="link" + Enter/Space.
  const rowNav = (id: string) => ({
    role: "link" as const,
    tabIndex: 0,
    onClick: () => navigate(`/admin/deleted-entities/${id}`),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        navigate(`/admin/deleted-entities/${id}`);
      }
    },
  });

  return (
    <div className="max-w-5xl mx-auto">
      {/* Page header */}
      <div className="mb-[18px]">
        <h1 className="text-[24px] leading-[1.1] font-display font-semibold text-text">
          {t("admin:deleted_page_title")}
        </h1>
        <p className="text-[13px] text-muted mt-[5px]">
          {t("admin:deleted_page_subtitle")}
        </p>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="skeleton h-16 rounded-[11px]" />
          ))}
        </div>
      )}

      {!isLoading && data.length === 0 && (
        <div className="text-dim text-[13px] p-[18px] text-center border border-dashed border-border rounded-[9px] bg-surface">
          {t("admin:no_deleted")}
        </div>
      )}

      {!isLoading && data.length > 0 && (
        <>
          {/* Mobile: card-list (< sm:) */}
          <ul className="space-y-3 sm:hidden">
            {data.map((e) => (
              <li key={e.id}>
                <div
                  {...rowNav(e.id)}
                  className="bg-surface border border-border rounded-[11px] shadow-card p-[14px_15px] space-y-1 block hover:shadow-card-hov transition-shadow cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                  aria-label={t("admin:preview_aria_label", { name: e.name })}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-[13.5px] text-text truncate">{e.name}</span>
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- presentational wrapper; the button inside handles keyboard activation */}
                    <span
                      role="presentation"
                      onClick={(ev) => ev.stopPropagation()}
                      onKeyDown={(ev) => ev.stopPropagation()}
                    >
                      <RestoreButton entity={e} />
                    </span>
                  </div>
                  <p className="text-[12.5px] text-muted">
                    {e.country?.flag} {e.country?.name ?? e.region}
                  </p>
                  <p className="text-[12px] text-dim">
                    {t("admin:deleted_by_label", {
                      actor: e.deletedBy ?? "—",
                      date: new Date(e.deletedAt).toLocaleDateString(i18n.language),
                    })}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          {/* Desktop: table (sm+) */}
          <div className="hidden sm:block bg-surface border border-border rounded-[11px] shadow-card overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-[11px] font-bold tracking-[.04em] uppercase text-dim px-[15px] py-[11px] bg-surface sticky top-0 z-[1]">
                    {t("admin:col_entity")}
                  </th>
                  <th className="text-left text-[11px] font-bold tracking-[.04em] uppercase text-dim px-[15px] py-[11px] bg-surface sticky top-0 z-[1]">
                    {t("admin:col_country")}
                  </th>
                  <th className="text-left text-[11px] font-bold tracking-[.04em] uppercase text-dim px-[15px] py-[11px] bg-surface sticky top-0 z-[1]">
                    {t("admin:col_region")}
                  </th>
                  <th className="text-left text-[11px] font-bold tracking-[.04em] uppercase text-dim px-[15px] py-[11px] bg-surface sticky top-0 z-[1]">
                    {t("admin:col_deleted_by")}
                  </th>
                  <th className="text-left text-[11px] font-bold tracking-[.04em] uppercase text-dim px-[15px] py-[11px] bg-surface sticky top-0 z-[1]">
                    {t("admin:col_deleted_on")}
                  </th>
                  <th className="px-[15px] py-[11px] bg-surface sticky top-0 z-[1]" />
                </tr>
              </thead>
              <tbody>
                {data.map((e) => (
                    <tr
                      key={e.id}
                      {...rowNav(e.id)}
                      aria-label={t("admin:preview_aria_label", { name: e.name })}
                      className="border-b border-border-2 last:border-b-0 hover:bg-surface-2 cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/40"
                    >
                      <td className="px-[15px] py-[13px] font-semibold text-[13.5px] text-text">
                        {e.name}
                      </td>
                      <td className="px-[15px] py-[13px] text-[13.5px] text-muted">
                        {e.country?.flag} {e.country?.name ?? "—"}
                      </td>
                      <td className="px-[15px] py-[13px] text-[13.5px] text-muted">
                        {e.region}
                      </td>
                      <td className="px-[15px] py-[13px] text-[13.5px] text-muted">
                        {e.deletedBy ?? "—"}
                      </td>
                      <td className="px-[15px] py-[13px] text-[13.5px] text-dim tabular-nums">
                        {new Date(e.deletedAt).toLocaleDateString(i18n.language)}
                      </td>
                      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- stops the row-nav from firing when interacting with the Restore control */}
                      <td
                        className="px-[15px] py-[13px] text-right"
                        onClick={(ev) => ev.stopPropagation()}
                        onKeyDown={(ev) => ev.stopPropagation()}
                      >
                        <RestoreButton entity={e} />
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
